import type { DialogProps } from '@radix-ui/react-dialog';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog/Dialog';

export function Command(commandProps: ComponentProps<typeof CommandPrimitive>) {
  const { className, ...props } = commandProps;
  return <CommandPrimitive data-slot="command" className={cn('flex h-full w-full flex-col overflow-hidden rounded-md', className)} {...props} />;
}

export function CommandDialog(commandDialogProps: DialogProps) {
  const { children, ...props } = commandDialogProps;
  return (
    <Dialog {...props}>
      <DialogContent className={'overflow-hidden p-0 shadow-lg'}>
        <Command
          className={
            '[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5'
          }
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export function CommandInput(
  commandInputProps: ComponentProps<typeof CommandPrimitive.Input> & {
    wrapperClassName?: string;
  }
) {
  const { className, wrapperClassName, disabled, ...props } = commandInputProps;

  return (
    <div data-slot="command-input-wrapper" data-disabled={disabled ? 'true' : undefined} className={cn('border-border-primary flex items-center border-b px-3', wrapperClassName)}>
      <SearchIcon className="text-muted-foreground mr-2 size-4 shrink-0" />
      <CommandPrimitive.Input
        data-slot="command-input"
        disabled={disabled}
        className={cn(
          'text-foreground md:text-sm placeholder:text-muted-foreground/50 flex h-10 w-full truncate rounded-md bg-transparent py-3 outline-hidden disabled:cursor-not-allowed disabled:opacity-60',
          className
        )}
        {...props}
      />
    </div>
  );
}

export function CommandList(commandListProps: ComponentProps<typeof CommandPrimitive.List>) {
  const { className, ...props } = commandListProps;
  return <CommandPrimitive.List data-slot="command-list" className={cn('max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto', className)} {...props} />;
}
export function CommandEmpty(commandEmptyProps: ComponentProps<typeof CommandPrimitive.Empty>) {
  const { className, ...props } = commandEmptyProps;

  return <CommandPrimitive.Empty data-slot="command-empty" className={cn('text-muted-foreground py-6 text-center text-sm', className)} {...props} />;
}

export function CommandLoading(commandLoadingProps: ComponentProps<typeof CommandPrimitive.Loading>) {
  const { className, ...props } = commandLoadingProps;

  return <CommandPrimitive.Loading data-slot="command-loading" className={cn('text-muted-foreground py-6 text-center text-sm', className)} {...props} />;
}

export function CommandGroup(commandGroupProps: ComponentProps<typeof CommandPrimitive.Group>) {
  const { className, ...props } = commandGroupProps;

  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-accent-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className
      )}
      {...props}
    />
  );
}

export function CommandSeparator(commandSeparatorProps: ComponentProps<typeof CommandPrimitive.Separator>) {
  const { className, ...props } = commandSeparatorProps;

  return <CommandPrimitive.Separator data-slot="command-separator" className={cn('bg-border-primary -mx-1 h-px', className)} {...props} />;
}

export function CommandItem(commandItemProps: ComponentProps<typeof CommandPrimitive.Item>) {
  const { className, ...props } = commandItemProps;

  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-accent/80 data-[selected=true]:text-foreground [&_svg:not([class*='text-'])]:text-foreground relative flex h-9 cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

export function CommandShortcut(commandShortcutProps: ComponentProps<'span'>) {
  const { className, ...props } = commandShortcutProps;
  return <span data-slot="command-shortcut" className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)} {...props} />;
}
